import Logger from "../logger"
import { Comparable } from "../util/Comparable"
import ObjectUtil from "../util/ObjectUtil"
import {
  Decision,
  DecisionReason,
  Experiment,
  FeatureFlagDecision,
  HackleUser,
  MatchValueType,
  RemoteConfigDecision
} from "../model/model"
import DecisionProcessor from "./DecisionProcessor"
import { Decisions } from "./Decisions"
import WorkspaceConfigFetcher from "../workspace/config/WorkspaceConfigFetcher"
import ExperimentLocalEvaluateRequest from "../evaluation/service/experiment/mode/local/ExperimentLocalEvaluateRequest"
import RemoteConfigLocalEvaluateRequest from "../evaluation/service/remoteconfig/mode/local/RemoteConfigLocalEvaluateRequest"
import EvaluateProcessor from "../evaluation/EvaluateProcessor"

const log = Logger.log

/**
 * Decisions from local evaluation against the on-device workspace config.
 */
export default class LocalDecisionProcessor implements DecisionProcessor {
  constructor(
    private readonly workspaceFetcher: WorkspaceConfigFetcher,
    private readonly evaluateProcessor: EvaluateProcessor
  ) {}

  experiment(experimentKey: number, user: HackleUser): Decision {
    if (!experimentKey) {
      log.error("experimentKey must not be empty")
      return Decision.of("A", DecisionReason.INVALID_INPUT)
    }

    const workspace = this.workspaceFetcher.workspace(user)

    if (!workspace) {
      log.warn("SDK not ready.")
      return Decision.of("A", DecisionReason.SDK_NOT_READY)
    }

    const experiment = workspace.getExperimentOrNull(experimentKey)

    if (!experiment) {
      log.warn("Experiment does not exist.")
      return Decision.of("A", DecisionReason.EXPERIMENT_NOT_FOUND)
    }

    const request = ExperimentLocalEvaluateRequest.of(workspace, user, experiment)
    return this.experimentDecision(request)
  }

  experiments(user: HackleUser): Comparable<Experiment, Decision> {
    const decisions = new Comparable<Experiment, Decision>((a, b) => a.id === b.id)
    const workspace = this.workspaceFetcher.workspace(user)
    if (!workspace) {
      return decisions
    }

    workspace.getExperiments().forEach((experiment) => {
      const request = ExperimentLocalEvaluateRequest.of(workspace, user, experiment, false)
      decisions.add(experiment, this.experimentDecision(request))
    })

    return decisions
  }

  private experimentDecision(request: ExperimentLocalEvaluateRequest): Decision {
    const evaluation = this.evaluateProcessor.experiment(request).evaluation
    return Decisions.experiment(evaluation)
  }

  featureFlag(featureKey: number, user: HackleUser): FeatureFlagDecision {
    if (!featureKey) {
      log.error("featureKey must not be empty")
      return FeatureFlagDecision.off(DecisionReason.INVALID_INPUT)
    }

    const workspace = this.workspaceFetcher.workspace(user)

    if (!workspace) {
      log.warn("SDK not ready.")
      return FeatureFlagDecision.off(DecisionReason.SDK_NOT_READY)
    }

    const featureFlag = workspace.getFeatureFlagOrNull(featureKey)

    if (!featureFlag) {
      log.warn("FeatureFlag does not exist.")
      return FeatureFlagDecision.off(DecisionReason.FEATURE_FLAG_NOT_FOUND)
    }

    const request = ExperimentLocalEvaluateRequest.of(workspace, user, featureFlag)
    return this.featureFlagDecision(request)
  }

  featureFlags(user: HackleUser): Comparable<Experiment, FeatureFlagDecision> {
    const decisions = new Comparable<Experiment, FeatureFlagDecision>((a, b) => a.id === b.id)
    const workspace = this.workspaceFetcher.workspace(user)

    if (!workspace) {
      return decisions
    }

    workspace.getFeatureFlags().forEach((featureFlag) => {
      const request = ExperimentLocalEvaluateRequest.of(workspace, user, featureFlag, false)
      decisions.add(featureFlag, this.featureFlagDecision(request))
    })

    return decisions
  }

  private featureFlagDecision(request: ExperimentLocalEvaluateRequest): FeatureFlagDecision {
    const evaluation = this.evaluateProcessor.experiment(request).evaluation
    return Decisions.featureFlag(evaluation)
  }

  remoteConfig(
    parameterKey: string,
    user: HackleUser,
    requiredType: MatchValueType,
    defaultValue: string | number | boolean
  ): RemoteConfigDecision {
    const workspace = this.workspaceFetcher.workspace(user)

    if (ObjectUtil.isNullOrUndefined(workspace)) {
      log.warn("SDK not ready.")
      return RemoteConfigDecision.of(defaultValue, DecisionReason.SDK_NOT_READY)
    }

    const remoteConfigParameter = workspace.getRemoteConfigParameterOrNull(parameterKey)

    if (ObjectUtil.isNullOrUndefined(remoteConfigParameter)) {
      log.warn("Remote config parameter does not exist.")
      return RemoteConfigDecision.of(defaultValue, DecisionReason.REMOTE_CONFIG_PARAMETER_NOT_FOUND)
    }

    const request = RemoteConfigLocalEvaluateRequest.of(workspace, user, remoteConfigParameter, requiredType)
    const evaluation = this.evaluateProcessor.remoteConfig(request).evaluation

    return Decisions.remoteConfig(evaluation, requiredType, defaultValue)
  }
}
